"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMarkupStore } from "../store";
import type { FeedbackItem, ViewportPreset } from "../types";
import { cn } from "../utils";
import { DEVICE_FRAME_NAME, useDeviceFrameStore } from "../utils/deviceFrame";
import { FeedbackPins } from "./FeedbackPins";
import { CloseIcon } from "./Icons";
import { DeviceIcon, PresetMenu, useDismiss } from "./ViewportControls";

interface DeviceStageProps {
    onPinClick: (feedback: FeedbackItem) => void;
}

type FrameStatus = "loading" | "ready" | "blocked" | "offsite";

/** Room kept clear on the right for the widget: the 500px panel plus its pill. */
const PANEL_GUTTER = 548;
/** Room kept clear on the right when only the pill is showing. */
const PILL_GUTTER = 56;
const TOOLBAR_HEIGHT = 72;
const STAGE_PADDING = 24;
/** How often to check the preview's URL for client-side navigation. */
const URL_POLL_MS = 400;

/**
 * The responsive preview: the current page, live, in an iframe the size of the
 * chosen device, with pins drawn over it and a toolbar to switch device, rotate,
 * reload or leave.
 *
 * The frame is loaded once and only resized on device switch, so switching is
 * instant and keeps scroll position and page state — media queries respond to
 * the new width exactly as they would on a resized window.
 */
export const DeviceStage: React.FC<DeviceStageProps> = ({ onPinClick }) => {
    const {
        viewportMode,
        setViewportMode,
        isVisible,
        isOpen,
        config,
        isSteppingAside,
    } = useMarkupStore();
    const { frame, url, setFrame, setUrl } = useDeviceFrameStore();

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const areaRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Captured once: the frame navigates itself after this, and changing `src`
    // would throw away whatever the reviewer had clicked through to.
    const [src] = useState(() => window.location.href);
    const [status, setStatus] = useState<FrameStatus>("loading");
    const [reloadKey, setReloadKey] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    // Whether any page of ours has loaded in the frame yet — tells "the site
    // won't be framed at all" apart from "the reviewer clicked off-site".
    const hasLoadedRef = useRef(false);
    // The frame document currently adopted, if any.
    const adoptedDocRef = useRef<Document | null>(null);
    const [area, setArea] = useState({ width: 0, height: 0 });
    useDismiss(menuOpen, setMenuOpen, menuRef);

    // ── Frame lifecycle ────────────────────────────────────────────────────

    const handleLoad = useCallback(() => {
        const win = iframeRef.current?.contentWindow ?? null;
        let href: string | null = null;
        try {
            // Throws when the document isn't ours: the site sent
            // X-Frame-Options / frame-ancestors (the browser swaps in an error
            // page), or the reviewer followed a link to another site.
            href = win?.location.href ?? null;
            void win?.document.body;
        } catch (_) {
            href = null;
        }

        if (!win || !href || href === "about:blank") {
            setFrame(null, null);
            setStatus(hasLoadedRef.current ? "offsite" : "blocked");
            return;
        }

        hasLoadedRef.current = true;
        setFrame(win, href);
        setStatus("ready");

        // Adopted early by the poller below, then `load` fires for the same
        // document: one pagehide listener per document is enough.
        const doc = win.document;
        if (adoptedDocRef.current === doc) return;
        adoptedDocRef.current = doc;

        // Leaving the page (link click, form post, reload inside the frame)
        // takes the document away from under pins and pickers; drop it now
        // rather than let them measure a dead one until the next load.
        win.addEventListener("pagehide", () => {
            adoptedDocRef.current = null;
            setFrame(null, null);
            setStatus("loading");
        });
    }, [setFrame]);

    // Adopt the page as soon as it's interactive rather than waiting for
    // `load`, which holds out for every image and script. The page is on
    // screen and clickable well before then; a pin placed in that gap used to
    // be ignored, because the picker had no document to listen on yet.
    useEffect(() => {
        if (status !== "loading") return;
        const id = window.setInterval(() => {
            try {
                const doc = iframeRef.current?.contentDocument;
                if (
                    doc &&
                    doc !== adoptedDocRef.current &&
                    doc.readyState !== "loading" &&
                    doc.location.href !== "about:blank"
                ) {
                    handleLoad();
                }
            } catch (_) {
                // Cross-origin: the load event decides (blocked / off-site).
            }
        }, 50);
        return () => window.clearInterval(id);
    }, [status, handleLoad]);

    // Client-side routing (Next.js <Link>, React Router…) changes the URL
    // without a load event. The frame's router is its own, so poll rather than
    // try to hook it.
    useEffect(() => {
        if (!frame) return;
        const id = window.setInterval(() => {
            try {
                const href = frame.location.href;
                if (href !== useDeviceFrameStore.getState().url) setUrl(href);
            } catch (_) {
                setFrame(null, null);
                setStatus("offsite");
            }
        }, URL_POLL_MS);
        return () => window.clearInterval(id);
    }, [frame, setUrl, setFrame]);

    // The preview is the page now: nothing should be left pointing at it once
    // it's gone.
    useEffect(() => () => setFrame(null, null), [setFrame]);

    // Esc leaves the preview — from either document, since focus is usually
    // inside the frame after the reviewer has interacted with the page. Keys
    // already claimed (pin placement, open menus) are left alone.
    const exit = useCallback(() => {
        // Follow the reviewer: if they navigated inside the preview, land the
        // real page there too instead of snapping back to where they started.
        const target = useDeviceFrameStore.getState().url;
        setViewportMode(null);
        if (target && target !== window.location.href) {
            try {
                if (new URL(target).origin !== window.location.origin) return;
            } catch (_) {
                return;
            }
            if (config.onNavigate) config.onNavigate(target);
            else window.location.assign(target);
        }
    }, [setViewportMode, config]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !e.defaultPrevented) exit();
        };
        const docs = [document];
        try {
            if (frame) docs.push(frame.document);
        } catch (_) {
            /* cross-origin — nothing to listen to */
        }
        docs.forEach((d) => d.addEventListener("keydown", onKey));
        return () =>
            docs.forEach((d) => {
                try {
                    d.removeEventListener("keydown", onKey);
                } catch (_) {
                    /* frame already gone */
                }
            });
    }, [frame, exit]);

    // The backdrop covers the page; stop it scrolling invisibly underneath.
    useEffect(() => {
        const html = document.documentElement;
        const previous = html.style.overflow;
        html.style.overflow = "hidden";
        return () => {
            html.style.overflow = previous;
        };
    }, []);

    // ── Fit the device to the space available ─────────────────────────────

    useEffect(() => {
        const el = areaRef.current;
        if (!el) return;
        const measure = () =>
            setArea({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    if (!viewportMode) return null;

    const { width, height } = viewportMode;
    // Never enlarge; shrink only as far as needed to fit. Pins are drawn in
    // the same scaled box, so they stay glued to the page at any zoom.
    const scale =
        area.width && area.height
            ? Math.min(1, area.width / width, area.height / height)
            : 1;
    const isLandscape = width > height;

    const selectPreset = (preset: ViewportPreset) => {
        // Keep the current orientation when switching device.
        const long = Math.max(preset.width, preset.height);
        const short = Math.min(preset.width, preset.height);
        setViewportMode({
            ...preset,
            width: isLandscape ? long : short,
            height: isLandscape ? short : long,
        });
        setMenuOpen(false);
    };

    const rotate = () =>
        setViewportMode({ ...viewportMode, width: height, height: width });

    const reload = () => {
        setFrame(null, null);
        setStatus("loading");
        try {
            iframeRef.current?.contentWindow?.location.reload();
        } catch (_) {
            // Off-site or blocked: remount the frame at the original page.
            setReloadKey((k) => k + 1);
        }
    };

    const path = (() => {
        try {
            const u = new URL(url ?? src);
            return u.pathname + u.search;
        } catch (_) {
            return "";
        }
    })();

    const rightGutter = !isVisible ? 0 : isOpen ? PANEL_GUTTER : PILL_GUTTER;

    return (
        <div
            className="markup-device-stage"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1,
                background: "#070c0f",
                pointerEvents: "auto",
                overscrollBehavior: "contain",
            }}
        >
            {/* ── Toolbar ── */}
            <div
                data-markup-viewport
                className="select-none"
                style={{
                    position: "absolute",
                    top: 16,
                    left: 0,
                    right: rightGutter,
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: "none",
                    // Out of the way while a screenshot is taken.
                    visibility: isSteppingAside ? "hidden" : undefined,
                }}
            >
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-2xl shadow-lg px-1.5 py-1.5 pointer-events-auto">
                    <div ref={menuRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-matter-ink/5 hover:bg-matter-ink/10 transition-colors"
                            title="Change device"
                        >
                            <DeviceIcon
                                deviceType={viewportMode.deviceType}
                                className="w-3.5 h-3.5 text-matter-accent shrink-0"
                            />
                            <span className="text-xs! font-semibold text-gray-800">
                                {viewportMode.name}
                            </span>
                            <span className="text-[10px] text-gray-500 tabular-nums">
                                {width}×{height}
                            </span>
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {menuOpen && (
                            <div className="absolute top-full left-0 mt-2">
                                <PresetMenu
                                    activeName={viewportMode.name}
                                    onSelect={selectPreset}
                                />
                            </div>
                        )}
                    </div>

                    <ToolbarButton onClick={rotate} title="Rotate">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </ToolbarButton>
                    <ToolbarButton onClick={reload} title="Reload preview">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12a7.5 7.5 0 0112.8-5.3L20 9.5M20 4.5v5h-5M19.5 12a7.5 7.5 0 01-12.8 5.3L4 14.5m0 5v-5h5" />
                    </ToolbarButton>

                    {path && (
                        <span
                            className="max-w-[220px] truncate px-2 text-[11px] text-gray-500 font-mono"
                            title={url ?? src}
                        >
                            {path}
                        </span>
                    )}

                    {scale < 1 && (
                        <span className="px-1.5 text-[10px] text-gray-400 tabular-nums" title="Scaled to fit your screen">
                            {Math.round(scale * 100)}%
                        </span>
                    )}

                    <div className="w-px h-5 bg-gray-200 mx-0.5" />

                    <button
                        type="button"
                        onClick={exit}
                        title="Exit responsive mode (Esc)"
                        className="flex items-center justify-center w-7 h-7 rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all"
                    >
                        <CloseIcon className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* ── Device ── */}
            <div
                ref={areaRef}
                style={{
                    position: "absolute",
                    top: TOOLBAR_HEIGHT,
                    left: STAGE_PADDING,
                    right: rightGutter + STAGE_PADDING,
                    bottom: STAGE_PADDING,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    transition: "right 300ms ease-in-out",
                }}
            >
                <div
                    style={{
                        width: width * scale,
                        height: height * scale,
                        position: "relative",
                        flexShrink: 0,
                        borderRadius: 18,
                        overflow: "hidden",
                        background: "#ffffff",
                        boxShadow:
                            "0 32px 96px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08)",
                    }}
                >
                    <div
                        style={{
                            width,
                            height,
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                            position: "relative",
                        }}
                    >
                        <iframe
                            key={reloadKey}
                            ref={iframeRef}
                            name={DEVICE_FRAME_NAME}
                            src={src}
                            title={`${viewportMode.name} preview`}
                            onLoad={handleLoad}
                            style={{
                                width,
                                height,
                                border: 0,
                                display: "block",
                                background: "#ffffff",
                            }}
                        />

                        {/* Pins, in a box exactly covering the frame's viewport. */}
                        {frame && status === "ready" && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    overflow: "hidden",
                                    pointerEvents: "none",
                                }}
                            >
                                <FeedbackPins
                                    view={frame}
                                    url={url ?? undefined}
                                    onPinClick={onPinClick}
                                />
                            </div>
                        )}

                        {status !== "ready" && (
                            <FrameNotice
                                status={status}
                                onReload={() => {
                                    setStatus("loading");
                                    setReloadKey((k) => k + 1);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ToolbarButton: React.FC<{
    onClick: () => void;
    title: string;
    children: React.ReactNode;
}> = ({ onClick, title, children }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className="flex items-center justify-center w-7 h-7 rounded-xl text-gray-400 hover:bg-matter-ink/5 hover:text-gray-700 transition-all"
    >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {children}
        </svg>
    </button>
);

const FrameNotice: React.FC<{ status: FrameStatus; onReload: () => void }> = ({
    status,
    onReload,
}) => {
    if (status === "loading") {
        return (
            <div
                className="flex items-center justify-center"
                style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.6)", pointerEvents: "none" }}
            >
                <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-matter-accent animate-spin" />
            </div>
        );
    }

    const copy =
        status === "blocked"
            ? {
                  title: "This page can't be previewed",
                  body: "The site refused to load inside a frame. Allow same-origin framing (X-Frame-Options: SAMEORIGIN, or frame-ancestors 'self' in your CSP) to use responsive mode.",
              }
            : {
                  title: "You've left the site",
                  body: "Pages on other domains can't be marked up. Go back to continue.",
              };

    return (
        <div
            className="flex flex-col items-center justify-center text-center gap-3 p-8 bg-white"
            style={{ position: "absolute", inset: 0 }}
        >
            <p className="text-sm! font-semibold text-gray-800">{copy.title}</p>
            <p className="text-xs! text-gray-500 leading-relaxed">{copy.body}</p>
            <button
                type="button"
                onClick={onReload}
                className={cn(
                    "mt-2 px-4 py-2 rounded-xl text-xs! font-semibold bg-matter-ink text-matter-cream hover:bg-matter-ink/80 transition-colors",
                )}
            >
                {status === "blocked" ? "Try again" : "Back to the site"}
            </button>
        </div>
    );
};
